import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import {
  useEffect,
  useMemo,
  useState,
} from "react"

import MainLayout from "../../layouts/MainLayout"

import {
  EmptyState,
  FilterBar,
  KpiCard,
  PageHeader,
  SectionCard,
} from "../../components/ui"

import {
  getDashboardProFormData,
  getDashboardProOverview,
} from "../../services/dashboardProService"

import {
  getBusinessLabel,
} from "../../config/appConfig"


// =========================================================
// BUSINESS LABELS
// =========================================================

const MAIN_COMPANY =
  getBusinessLabel(
    "mainCompany",
    "Entreprise principale"
  )

const SHOP =
  getBusinessLabel(
    "shop",
    "Shop"
  )

const SHOPS =
  getBusinessLabel(
    "shops",
    "Shops"
  )


// =========================================================
// CHANNELS
// =========================================================

const DEFAULT_CHANNELS = [
  {
    value: "central",
    label: "Distribution centrale",
  },
  {
    value: "distributor",
    label: "Distribution principale",
  },
  {
    value: "relay",
    label: "Structure secondaire",
  },
  {
    value: "agents",
    label: "Agents commerciaux",
  },
]

const CHANNEL_ORGANIZATION_TYPES = {
  central: "kin_mousse",
  distributor: "distributor",
  relay: "relay_depot",
}

const CHART_COLORS = [
  "#2563eb",
  "#16a34a",
  "#f97316",
  "#7c3aed",
  "#0891b2",
]


// =========================================================
// DEFAULT FILTERS
// =========================================================

const today =
  new Date()

const toDateInput = (
  date
) =>
  date
    .toISOString()
    .slice(
      0,
      10
    )

const defaultFilters = {
  date_from:
    toDateInput(
      new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 7
      )
    ),

  date_to:
    toDateInput(
      today
    ),

  channel:
    "",

  store_id:
    "",

  limit:
    5,
}


// =========================================================
// HELPERS
// =========================================================

const cleanParams = (
  params = {}
) => {
  const cleaned = {}

  Object.entries(
    params
  ).forEach(
    ([
      key,
      value,
    ]) => {
      if (
        value !== "" &&
        value !== null &&
        value !== undefined
      ) {
        cleaned[key] =
          value
      }
    }
  )

  return cleaned
}


function toNumber(
  value
) {
  const number =
    Number(
      value || 0
    )

  return Number.isFinite(
    number
  )
    ? number
    : 0
}


function money(
  value
) {
  return toNumber(
    value
  ).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits:
        2,

      maximumFractionDigits:
        2,
    }
  )
}


function qty(
  value
) {
  return toNumber(
    value
  ).toLocaleString(
    "fr-FR",
    {
      maximumFractionDigits:
        2,
    }
  )
}


function formatDate(
  value
) {
  if (
    !value
  ) {
    return "-"
  }

  const date =
    new Date(
      value
    )

  return Number.isNaN(
    date.getTime()
  )
    ? String(
        value
      )
    : date.toLocaleDateString(
        "fr-FR"
      )
}


function shortDate(
  value
) {
  if (
    !value
  ) {
    return ""
  }

  const date =
    new Date(
      value
    )

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(
      value
    )
  }

  return date.toLocaleDateString(
    "fr-FR",
    {
      day: "2-digit",
      month: "2-digit",
    }
  )
}


// =========================================================
// TABLE
// =========================================================

function DataTable({
  columns,
  rows,
  emptyTitle = "Aucune donnée",
  emptyText = "Aucun élément disponible pour la période sélectionnée.",
  renderRow,
  minWidth = 760,
}) {
  if (
    !rows.length
  ) {
    return (
      <EmptyState
        title={
          emptyTitle
        }
        description={
          emptyText
        }
        minHeight={
          170
        }
      />
    )
  }

  return (
    <Box
      sx={{
        width:
          "100%",

        minWidth:
          0,

        overflowX:
          "auto",
      }}
    >
      <Table
        size="small"

        sx={{
          minWidth,

          "& .MuiTableCell-root":
            {
              py:
                1.15,

              px:
                1.25,

              whiteSpace:
                "nowrap",
            },
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map(
              (
                column
              ) => (
                <TableCell
                  key={
                    column
                  }
                >
                  {column}
                </TableCell>
              )
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map(
            renderRow
          )}
        </TableBody>
      </Table>
    </Box>
  )
}


// =========================================================
// DASHBOARD
// =========================================================

export default function DashboardProPage() {
  const [
    filters,
    setFilters,
  ] = useState(
    defaultFilters
  )

  const [
    stores,
    setStores,
  ] = useState([])

  const [
    channels,
    setChannels,
  ] = useState(
    DEFAULT_CHANNELS
  )

  const [
    data,
    setData,
  ] = useState(
    null
  )

  const [
    loading,
    setLoading,
  ] = useState(
    false
  )

  const [
    errorMessage,
    setErrorMessage,
  ] = useState(
    ""
  )


  const loadData =
    async (
      customFilters =
        filters
    ) => {
      try {
        setLoading(
          true
        )

        setErrorMessage(
          ""
        )

        const params =
          cleanParams({
            ...customFilters,

            limit:
              Number(
                customFilters.limit ||
                  5
              ),
          })

        const [
          formData,
          overview,
        ] =
          await Promise.all([
            getDashboardProFormData(),

            getDashboardProOverview(
              params
            ),
          ])

        setStores(
          Array.isArray(
            formData?.stores
          )
            ? formData.stores
            : []
        )

        setChannels(
          Array.isArray(
            formData?.channels
          ) &&
          formData.channels.length
            ? formData.channels
            : DEFAULT_CHANNELS
        )

        setData(
          overview ||
            {}
        )
      } catch (
        error
      ) {
        console.error(
          error
        )

        setErrorMessage(
          error?.response?.data?.detail ||
            error?.message ||
            "Impossible de charger la vue d'ensemble."
        )
      } finally {
        setLoading(
          false
        )
      }
    }


  useEffect(
    () => {
      loadData(
        defaultFilters
      )
    },
    []
  )


  const handleChange = (
    field,
    value
  ) => {
    setFilters(
      (
        current
      ) => ({
        ...current,

        [field]:
          value,
      })
    )
  }


  const handleChannelChange = (
    value
  ) => {
    setFilters(
      (
        current
      ) => ({
        ...current,

        channel:
          value,

        store_id:
          "",
      })
    )
  }


  const handleReset =
    () => {
      setFilters(
        defaultFilters
      )

      loadData(
        defaultFilters
      )
    }


  // =======================================================
  // DATA
  // =======================================================

  const sales =
    data?.sales ||
    {}

  const stock =
    data?.stock ||
    {}

  const financial =
    data?.financial ||
    {}


  const dailySales =
    useMemo(
      () =>
        Array.isArray(
          sales.daily
        )
          ? sales.daily
          : [],
      [
        sales.daily,
      ]
    )


  const salesByChannel =
    useMemo(
      () =>
        Array.isArray(
          sales.by_channel
        )
          ? sales.by_channel
          : [],
      [
        sales.by_channel,
      ]
    )


  const topProducts =
    useMemo(
      () =>
        sales.top_products ||
        [],
      [
        sales.top_products,
      ]
    )


  const topShops =
    useMemo(
      () =>
        sales.top_shops ||
        [],
      [
        sales.top_shops,
      ]
    )


  const topClients =
    useMemo(
      () =>
        sales.top_clients ||
        [],
      [
        sales.top_clients,
      ]
    )


  const lowStock =
    useMemo(
      () =>
        stock.low_stock ||
        [],
      [
        stock.low_stock,
      ]
    )


  const stockByOrganization =
    useMemo(
      () =>
        stock.stock_by_organization ||
        [],
      [
        stock.stock_by_organization,
      ]
    )


  const organizationDebts =
    useMemo(
      () =>
        financial.organization_debts ||
        [],
      [
        financial.organization_debts,
      ]
    )


  const debts =
    useMemo(
      () =>
        financial.debts ||
        [],
      [
        financial.debts,
      ]
    )


  const filteredStores =
    useMemo(
      () => {
        if (
          !filters.channel
        ) {
          return stores
        }

        if (
          filters.channel ===
          "agents"
        ) {
          return []
        }

        const expectedType =
          CHANNEL_ORGANIZATION_TYPES[
            filters.channel
          ]

        if (
          !expectedType
        ) {
          return stores
        }

        return stores.filter(
          (
            store
          ) =>
            String(
              store.organization_type ||
                ""
            ).toLowerCase() ===
            expectedType
        )
      },
      [
        stores,
        filters.channel,
      ]
    )


  const selectedChannelLabel =
    useMemo(
      () => {
        if (
          !filters.channel
        ) {
          return "Tous les canaux"
        }

        return (
          channels.find(
            (
              item
            ) =>
              item.value ===
              filters.channel
          )?.label ||
          "Canal sélectionné"
        )
      },
      [
        channels,
        filters.channel,
      ]
    )


  // =======================================================
  // KPI VALUES
  // =======================================================

  const salesAmount =
    toNumber(
      sales.total_sales_amount
    )

  const paidAmount =
    toNumber(
      sales.total_paid_amount
    )

  const remainingAmount =
    toNumber(
      sales.total_remaining_amount
    )

  const stockSaleValue =
    toNumber(
      stock.stock_sale_value
    )

  const stockPurchaseValue =
    toNumber(
      stock.stock_purchase_value
    )

  const stockMargin =
    toNumber(
      stock.stock_potential_margin
    )

  const openDebts =
    toNumber(
      financial.total_open_debts
    )


  // =======================================================
  // CHART DATA
  // =======================================================

  const dailyChartData =
    useMemo(
      () =>
        dailySales.map(
          (
            row
          ) => ({
            date:
              shortDate(
                row.day
              ),

            ventes:
              toNumber(
                row.total_amount
              ),

            encaisse:
              toNumber(
                row.paid_amount
              ),
          })
        ),
      [
        dailySales,
      ]
    )


  const channelChartData =
    useMemo(
      () =>
        salesByChannel
          .filter(
            (
              row
            ) =>
              toNumber(
                row.total_amount
              ) > 0 ||
              toNumber(
                row.sales_count
              ) > 0
          )
          .map(
            (
              row
            ) => ({
              name:
                row.channel_label ||
                row.channel ||
                "Autres",

              value:
                toNumber(
                  row.total_amount
                ),

              count:
                toNumber(
                  row.sales_count
                ),
            })
          ),
      [
        salesByChannel,
      ]
    )


  // =======================================================
  // UI
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          width:
            "100%",

          minWidth:
            0,

          maxWidth:
            "100%",
        }}
      >
        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <PageHeader
          eyebrow="Pilotage"
          title="Vue d’ensemble"
          subtitle={`Vision consolidée de l'activité commerciale, financière et stock. Périmètre actuel : ${selectedChannelLabel}.`}
          sx={{
            mb:
              1.5,
          }}
        />


        {/* ================================================= */}
        {/* FILTERS */}
        {/* ================================================= */}

        <FilterBar
          sx={{
            mb:
              2.25,
          }}

          columns="1fr 1fr 1.1fr 1.35fr .7fr auto auto"
        >
          <TextField
            label="Date début"
            type="date"
            fullWidth

            value={
              filters.date_from
            }

            onChange={(
              event
            ) =>
              handleChange(
                "date_from",
                event.target.value
              )
            }

            slotProps={{
              inputLabel: {
                shrink:
                  true,
              },
            }}
          />

          <TextField
            label="Date fin"
            type="date"
            fullWidth

            value={
              filters.date_to
            }

            onChange={(
              event
            ) =>
              handleChange(
                "date_to",
                event.target.value
              )
            }

            slotProps={{
              inputLabel: {
                shrink:
                  true,
              },
            }}
          />

          <TextField
            select
            label="Canal"
            fullWidth

            value={
              filters.channel
            }

            onChange={(
              event
            ) =>
              handleChannelChange(
                event.target.value
              )
            }
          >
            <MenuItem
              value=""
            >
              Tous les canaux
            </MenuItem>

            {channels.map(
              (
                channel
              ) => (
                <MenuItem
                  key={
                    channel.value
                  }
                  value={
                    channel.value
                  }
                >
                  {channel.label}
                </MenuItem>
              )
            )}
          </TextField>

          <TextField
            select
            label={SHOP}
            fullWidth

            value={
              filters.store_id
            }

            disabled={
              filters.channel ===
              "agents"
            }

            onChange={(
              event
            ) =>
              handleChange(
                "store_id",
                event.target.value
              )
            }
          >
            <MenuItem
              value=""
            >
              {filters.channel ===
              "agents"
                ? "Non applicable aux agents"
                : `Tous les ${SHOPS.toLowerCase()}`}
            </MenuItem>

            {filteredStores.map(
              (
                store
              ) => (
                <MenuItem
                  key={
                    store.id
                  }
                  value={
                    store.id
                  }
                >
                  {store.code
                    ? `${store.code} - ${store.name}`
                    : store.name}
                </MenuItem>
              )
            )}
          </TextField>

          <TextField
            select
            label="Top"
            fullWidth

            value={
              filters.limit
            }

            onChange={(
              event
            ) =>
              handleChange(
                "limit",
                event.target.value
              )
            }
          >
            <MenuItem
              value={3}
            >
              Top 3
            </MenuItem>

            <MenuItem
              value={5}
            >
              Top 5
            </MenuItem>

            <MenuItem
              value={10}
            >
              Top 10
            </MenuItem>
          </TextField>

          <Button
            variant="contained"

            onClick={() =>
              loadData(
                filters
              )
            }

            disabled={
              loading
            }
          >
            {loading
              ? "Chargement..."
              : "Appliquer"}
          </Button>

          <Button
            variant="outlined"

            onClick={
              handleReset
            }

            disabled={
              loading
            }
          >
            Réinitialiser
          </Button>
        </FilterBar>


        {errorMessage ? (
          <Alert
            severity="error"

            sx={{
              mb:
                2,
            }}
          >
            {errorMessage}
          </Alert>
        ) : null}


        {/* ================================================= */}
        {/* PERFORMANCE */}
        {/* ================================================= */}

        <Box
          sx={{
            mb:
              1,
          }}
        >
          <Typography
            variant="h6"

            sx={{
              fontSize:
                17,

              fontWeight:
                900,
            }}
          >
            Performance commerciale
          </Typography>

          <Typography
            color="text.secondary"

            sx={{
              mt:
                0.2,

              fontSize:
                12.5,
            }}
          >
            Ventes, encaissements, créances clients et dettes sur le périmètre sélectionné.
          </Typography>
        </Box>


        <Box
          sx={{
            display:
              "grid",

            gridTemplateColumns:
              {
                xs:
                  "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                xl:
                  "repeat(4, minmax(0, 1fr))",
              },

            gap:
              1.5,

            mb:
              2.5,
          }}
        >
          <KpiCard
            label="Chiffre d'affaires"
            value={
              money(
                salesAmount
              )
            }
            helper={`${qty(
              sales.total_sales_count
            )} vente(s)`}
          />

          <KpiCard
            label="Encaissements"
            value={
              money(
                paidAmount
              )
            }
            helper="Paiements clients reçus"
            accent="success"
          />

          <KpiCard
            label="Reste clients"
            value={
              money(
                remainingAmount
              )
            }
            helper="Créances commerciales"
            accent="warning"
          />

          <KpiCard
            label="Dettes ouvertes"
            value={
              money(
                openDebts
              )
            }
            helper="Montant restant à régler"
            accent="error"
          />
        </Box>


        {/* ================================================= */}
        {/* STOCK */}
        {/* ================================================= */}

        <Box
          sx={{
            mb:
              1,
          }}
        >
          <Typography
            variant="h6"

            sx={{
              fontSize:
                17,

              fontWeight:
                900,
            }}
          >
            Stock & rentabilité potentielle
          </Typography>

          <Typography
            color="text.secondary"

            sx={{
              mt:
                0.2,

              fontSize:
                12.5,
            }}
          >
            Valeur du stock, marge potentielle et références nécessitant une attention.
          </Typography>
        </Box>


        <Box
          sx={{
            display:
              "grid",

            gridTemplateColumns:
              {
                xs:
                  "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                xl:
                  "repeat(4, minmax(0, 1fr))",
              },

            gap:
              1.5,

            mb:
              2.5,
          }}
        >
          <KpiCard
            label="Valeur stock vente"
            value={
              money(
                stockSaleValue
              )
            }
            helper={`${qty(
              stock.total_stock
            )} unité(s)`}
          />

          <KpiCard
            label="Valeur stock achat"
            value={
              money(
                stockPurchaseValue
              )
            }
            helper={`Coût ${MAIN_COMPANY}`}
          />

          <KpiCard
            label="Marge potentielle"
            value={
              money(
                stockMargin
              )
            }
            helper="Valeur potentielle du stock restant"
            accent="success"
          />

          <KpiCard
            label="Stock faible"
            value={
              qty(
                stock.low_stock_count
              )
            }
            helper={`${qty(
              stock.products_in_stock
            )} référence(s) en stock`}
            accent="warning"
          />
        </Box>


        {/* ================================================= */}
        {/* CHARTS */}
        {/* ================================================= */}

        <Box
          sx={{
            display:
              "grid",

            gridTemplateColumns:
              {
                xs:
                  "1fr",

                xl:
                  "minmax(0, 1.35fr) minmax(340px, .65fr)",
              },

            gap:
              1.5,

            mb:
              2.5,
          }}
        >
          <SectionCard
            title="Évolution des ventes"
            subtitle="Chiffre d'affaires et encaissements par jour."
          >
            {dailyChartData.length ? (
              <Box
                sx={{
                  height: {
                    xs:
                      260,

                    md:
                      300,
                  },
                }}
              >
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >
                  <BarChart
                    data={
                      dailyChartData
                    }

                    margin={{
                      top: 10,
                      right: 12,
                      left: 4,
                      bottom: 0,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                    />

                    <XAxis
                      dataKey="date"
                      tick={{
                        fontSize: 11,
                      }}
                    />

                    <YAxis
                      tick={{
                        fontSize: 11,
                      }}
                    />

                    <Tooltip
                      formatter={(
                        value
                      ) =>
                        money(
                          value
                        )
                      }
                    />

                    <Legend />

                    <Bar
                      dataKey="ventes"
                      name="Ventes"
                      fill="#2563eb"
                      radius={[
                        5,
                        5,
                        0,
                        0,
                      ]}
                    />

                    <Bar
                      dataKey="encaisse"
                      name="Encaissé"
                      fill="#16a34a"
                      radius={[
                        5,
                        5,
                        0,
                        0,
                      ]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <EmptyState
                title="Aucune évolution disponible"
                description="Les ventes apparaîtront ici dès qu'elles seront disponibles sur la période."
                minHeight={260}
              />
            )}
          </SectionCard>


          <SectionCard
            title="Répartition par canal"
            subtitle="Poids commercial des différents circuits de distribution."
          >
            {channelChartData.length ? (
              <Box
                sx={{
                  height: {
                    xs:
                      260,

                    md:
                      300,
                  },
                }}
              >
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >
                  <PieChart>
                    <Pie
                      data={
                        channelChartData
                      }

                      dataKey="value"
                      nameKey="name"

                      cx="50%"
                      cy="45%"

                      innerRadius={58}
                      outerRadius={92}

                      paddingAngle={2}
                    >
                      {channelChartData.map(
                        (
                          entry,
                          index
                        ) => (
                          <Cell
                            key={`${entry.name}-${index}`}
                            fill={
                              CHART_COLORS[
                                index %
                                  CHART_COLORS.length
                              ]
                            }
                          />
                        )
                      )}
                    </Pie>

                    <Tooltip
                      formatter={(
                        value
                      ) =>
                        money(
                          value
                        )
                      }
                    />

                    <Legend
                      verticalAlign="bottom"
                      height={42}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <EmptyState
                title="Aucune répartition disponible"
                description="La répartition par canal apparaîtra dès que des ventes seront enregistrées."
                minHeight={260}
              />
            )}
          </SectionCard>
        </Box>


        {/* ================================================= */}
        {/* DETAILS */}
        {/* ================================================= */}

        <Box
          sx={{
            display:
              "grid",

            gridTemplateColumns:
              {
                xs:
                  "1fr",

                xl:
                  "repeat(2, minmax(0, 1fr))",
              },

            gap:
              1.5,
          }}
        >
          <SectionCard
            title="Ventes par jour"
            subtitle="Détail quotidien des ventes, encaissements et restes clients."
            noPadding
          >
            <DataTable
              columns={[
                "Date",
                "Ventes",
                "Montant",
                "Payé",
                "Reste",
              ]}

              rows={
                dailySales
              }

              emptyTitle="Aucune vente"
              emptyText="Aucune vente n'est disponible pour la période sélectionnée."

              renderRow={(
                row,
                index
              ) => (
                <TableRow
                  key={`${row.day}-${index}`}
                >
                  <TableCell>
                    {formatDate(
                      row.day
                    )}
                  </TableCell>

                  <TableCell>
                    {qty(
                      row.sales_count
                    )}
                  </TableCell>

                  <TableCell>
                    {money(
                      row.total_amount
                    )}
                  </TableCell>

                  <TableCell>
                    {money(
                      row.paid_amount
                    )}
                  </TableCell>

                  <TableCell>
                    {money(
                      row.remaining_amount
                    )}
                  </TableCell>
                </TableRow>
              )}
            />
          </SectionCard>


          <SectionCard
            title={`Top ${SHOPS.toLowerCase()}`}
            subtitle="Comparaison des points de vente du périmètre sélectionné."
            noPadding
          >
            <DataTable
              columns={[
                SHOP,
                "Organisation",
                "Ventes",
                "CA",
                "Payé",
                "Reste",
              ]}

              rows={
                topShops
              }

              emptyTitle={`Aucun ${SHOP.toLowerCase()}`}
              emptyText="Aucune activité commerciale n'a été détectée."

              renderRow={(
                row
              ) => (
                <TableRow
                  key={
                    row.store_id
                  }
                >
                  <TableCell>
                    <b>
                      {row.store_code}
                    </b>{" "}
                    -{" "}
                    {row.store_name}
                  </TableCell>

                  <TableCell>
                    {row.organization_name ||
                      "-"}
                  </TableCell>

                  <TableCell>
                    {qty(
                      row.sales_count
                    )}
                  </TableCell>

                  <TableCell>
                    {money(
                      row.total_sales_amount
                    )}
                  </TableCell>

                  <TableCell>
                    {money(
                      row.total_paid_amount
                    )}
                  </TableCell>

                  <TableCell>
                    {money(
                      row.total_remaining_amount
                    )}
                  </TableCell>
                </TableRow>
              )}
            />
          </SectionCard>


          <SectionCard
            title="Top produits"
            subtitle="Produits générant le plus d'activité commerciale."
            noPadding
          >
            <DataTable
              columns={[
                "Produit",
                "Qté",
                "Montant",
              ]}

              rows={
                topProducts
              }

              emptyTitle="Aucun produit vendu"
              emptyText="Aucun produit ne ressort sur la période sélectionnée."

              renderRow={(
                row
              ) => (
                <TableRow
                  key={
                    row.product_id ||
                    row.product_code
                  }
                >
                  <TableCell>
                    <b>
                      {row.product_code}
                    </b>{" "}
                    -{" "}
                    {row.product_name}
                  </TableCell>

                  <TableCell>
                    {qty(
                      row.total_quantity
                    )}
                  </TableCell>

                  <TableCell>
                    {money(
                      row.total_sales_amount
                    )}
                  </TableCell>
                </TableRow>
              )}
            />
          </SectionCard>


          <SectionCard
            title="Stock par organisation"
            subtitle="Répartition des quantités et des valeurs de stock."
            noPadding
          >
            <DataTable
              columns={[
                "Organisation",
                "Type",
                "Stock",
                "Valeur achat",
                "Valeur vente",
              ]}

              rows={
                stockByOrganization
              }

              emptyTitle="Aucun stock"
              emptyText="Aucune donnée de stock n'est disponible pour ce canal."

              renderRow={(
                row,
                index
              ) => (
                <TableRow
                  key={`${row.organization_id}-${index}`}
                >
                  <TableCell>
                    <b>
                      {row.organization_code ||
                        "-"}
                    </b>{" "}
                    -{" "}
                    {row.organization_name ||
                      "-"}
                  </TableCell>

                  <TableCell>
                    {row.organization_type ||
                      "-"}
                  </TableCell>

                  <TableCell>
                    {qty(
                      row.total_stock
                    )}
                  </TableCell>

                  <TableCell>
                    {money(
                      row.stock_purchase_value
                    )}
                  </TableCell>

                  <TableCell>
                    {money(
                      row.stock_sale_value
                    )}
                  </TableCell>
                </TableRow>
              )}
            />
          </SectionCard>


          <SectionCard
            title="Dettes par organisation"
            subtitle="Montants initiaux, réglés et encore dus."
            noPadding
          >
            <DataTable
              columns={[
                "Organisation",
                "Type",
                "Initial",
                "Payé",
                "Restant",
              ]}

              rows={
                organizationDebts
              }

              emptyTitle="Aucune dette"
              emptyText="Aucune dette organisationnelle n'est disponible."

              renderRow={(
                row,
                index
              ) => (
                <TableRow
                  key={`${row.organization_id}-${index}`}
                >
                  <TableCell>
                    <b>
                      {row.organization_code ||
                        "-"}
                    </b>{" "}
                    -{" "}
                    {row.organization_name ||
                      "-"}
                  </TableCell>

                  <TableCell>
                    {row.organization_type ||
                      "-"}
                  </TableCell>

                  <TableCell>
                    {money(
                      row.original_amount
                    )}
                  </TableCell>

                  <TableCell>
                    {money(
                      row.paid_amount
                    )}
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={
                        money(
                          row.remaining_amount
                        )
                      }

                      color={
                        toNumber(
                          row.remaining_amount
                        ) > 0
                          ? "error"
                          : "success"
                      }

                      size="small"
                    />
                  </TableCell>
                </TableRow>
              )}
            />
          </SectionCard>


          <SectionCard
            title="Stock faible"
            subtitle="Références proches ou sous leur seuil de réapprovisionnement."
            noPadding
          >
            <DataTable
              columns={[
                "Produit",
                "Lieu",
                "Stock",
                "Seuil",
              ]}

              rows={
                lowStock
              }

              emptyTitle="Aucune alerte stock"
              emptyText="Aucun produit n'est sous le seuil de réapprovisionnement."

              renderRow={(
                row,
                index
              ) => (
                <TableRow
                  key={`${row.product_code}-${index}`}
                >
                  <TableCell>
                    <b>
                      {row.product_code}
                    </b>{" "}
                    -{" "}
                    {row.product_name}
                  </TableCell>

                  <TableCell>
                    {row.store_name ||
                      row.warehouse_name ||
                      "-"}
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={
                        qty(
                          row.stock_quantity
                        )
                      }
                      color="warning"
                      size="small"
                    />
                  </TableCell>

                  <TableCell>
                    {qty(
                      row.reorder_level
                    )}
                  </TableCell>
                </TableRow>
              )}
            />
          </SectionCard>


          <SectionCard
            title="Dettes ouvertes"
            subtitle="Dettes actuellement en cours de règlement."
            noPadding
          >
            <DataTable
              columns={[
                "Code",
                "Organisation",
                "Type",
                "Reste",
                "Statut",
              ]}

              rows={
                debts
              }

              emptyTitle="Aucune dette ouverte"
              emptyText="Aucune dette active n'a été détectée."

              renderRow={(
                debt,
                index
              ) => (
                <TableRow
                  key={`${debt.debt_code}-${index}`}
                >
                  <TableCell>
                    {debt.debt_code}
                  </TableCell>

                  <TableCell>
                    {debt.organization_name ||
                      debt.store_name ||
                      "-"}
                  </TableCell>

                  <TableCell>
                    {debt.debt_type ||
                      "-"}
                  </TableCell>

                  <TableCell>
                    {money(
                      debt.remaining_amount
                    )}
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={
                        debt.status ||
                        "-"
                      }
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              )}
            />
          </SectionCard>


          <SectionCard
            title="Top clients"
            subtitle="Clients générant les montants commerciaux les plus élevés."
            noPadding
          >
            <DataTable
              columns={[
                "Client",
                "Téléphone",
                "Ventes",
                "Montant",
              ]}

              rows={
                topClients
              }

              emptyTitle="Aucun client"
              emptyText="Aucun client ne ressort sur la période sélectionnée."

              renderRow={(
                client,
                index
              ) => (
                <TableRow
                  key={`${client.customer_id}-${index}`}
                >
                  <TableCell>
                    {client.customer_name ||
                      "-"}
                  </TableCell>

                  <TableCell>
                    {client.customer_phone ||
                      "-"}
                  </TableCell>

                  <TableCell>
                    {qty(
                      client.sales_count
                    )}
                  </TableCell>

                  <TableCell>
                    {money(
                      client.total_sales_amount
                    )}
                  </TableCell>
                </TableRow>
              )}
            />
          </SectionCard>
        </Box>
      </Box>
    </MainLayout>
  )
}
